import { useCallback, useEffect, useState } from 'react';

import Icon from './Icon.jsx';

/**
 * Fullscreen toggle.
 *
 * Deliberately the quietest control in the header: no label, no colour until
 * it is either hovered or actually doing something, and it disappears entirely
 * where fullscreen is not available. Somebody who never wants it should be able
 * to use the app for a year without noticing it is there.
 *
 * ── Three things this has to get right ──────────────────────────────────────
 *
 * **It hides when it cannot work.** iPhone Safari has no Element fullscreen at
 * all — `requestFullscreen` is simply absent, and `webkitFullscreenEnabled` is
 * false because only <video> may go fullscreen there. Same story inside an
 * iframe served without `allowfullscreen`. A button that silently does nothing
 * is worse than no button, so where the API is missing this renders `null`.
 *
 * **It follows the browser rather than its own memory.** Fullscreen can end
 * without this button being touched — Esc, F11, the OS, a tab switch — so the
 * icon is derived from `document.fullscreenElement` on every `fullscreenchange`
 * instead of from a boolean we flipped ourselves. State that guesses would
 * strand the button showing "exit" on a window that is no longer fullscreen.
 *
 * **The click stays a gesture.** `requestFullscreen` is only permitted from a
 * user activation, so nothing is awaited before calling it. The promise it
 * returns rejects (rather than throwing) when a browser refuses; that is caught
 * and dropped, because the only honest response is to leave the icon where the
 * `fullscreenchange` event puts it.
 */

/** The vendor spellings, in the order to try them. Safari desktop is webkit. */
function element() {
  return document.fullscreenElement ?? document.webkitFullscreenElement ?? null;
}

function enter(node) {
  const request = node.requestFullscreen ?? node.webkitRequestFullscreen;
  return request ? request.call(node) : null;
}

function leave() {
  const exit = document.exitFullscreen ?? document.webkitExitFullscreen;
  return exit ? exit.call(document) : null;
}

export default function FullscreenButton() {
  // null while we have not looked yet, so the first paint does not flash a
  // button onto a browser that is about to turn out not to support one.
  const [supported, setSupported] = useState(null);
  const [active, setActive] = useState(false);

  useEffect(() => {
    const root = document.documentElement;
    // `fullscreenEnabled` is the permission answer (iframe policy, iOS), the
    // method check is the existence one. Both have to hold.
    const canFullscreen =
      (document.fullscreenEnabled ?? document.webkitFullscreenEnabled ?? false) &&
      !!(root.requestFullscreen ?? root.webkitRequestFullscreen);

    setSupported(canFullscreen);
    if (!canFullscreen) return undefined;

    const sync = () => setActive(!!element());
    sync(); // In case we mounted into an already-fullscreen window.

    document.addEventListener('fullscreenchange', sync);
    document.addEventListener('webkitfullscreenchange', sync);
    return () => {
      document.removeEventListener('fullscreenchange', sync);
      document.removeEventListener('webkitfullscreenchange', sync);
    };
  }, []);

  const toggle = useCallback(() => {
    try {
      const result = element() ? leave() : enter(document.documentElement);
      // A rejected promise here is a refusal, not a crash — swallow it, and let
      // the fullscreenchange listener remain the source of truth for the icon.
      if (result?.catch) result.catch(() => {});
    } catch {
      /* Older synchronous-throwing implementations. Same answer: do nothing. */
    }
  }, []);

  if (!supported) return null;

  return (
    <button
      type="button"
      className={`fs-btn${active ? ' is-active' : ''}`}
      onClick={toggle}
      aria-pressed={active}
      aria-label={active ? 'Leave fullscreen' : 'Go fullscreen'}
      title={active ? 'Leave fullscreen (Esc)' : 'Fullscreen'}
    >
      <Icon name={active ? 'collapse' : 'expand'} size={17} />
    </button>
  );
}
